import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Upload, Receipt, Bell, DollarSign, Store, Wallet, ArrowRight, TrendingUp, TrendingDown, Building2, X, HandCoins } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '../components/PageHeader';
import { PeriodPicker, useSharedPeriod, periodToParams } from '../components/PeriodPicker';
import { MonthEndReconciliationBanner } from '../components/MonthEndReconciliationBanner';
import { api, fmtDate, fmtMoney, fmtDateTime, fmtDelta, fmtPercent } from '../lib/api';

function Kpi({ icon: Icon, label, value, hint, testid }) {
    return (
        <Card data-testid={testid} className="p-4 md:p-5 border-border/80">
            <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <Icon size={18} strokeWidth={1.75} />
                </div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            </div>
            <div className="mt-3 font-heading text-2xl md:text-3xl tabular-nums" data-testid={`${testid}-value`}>
                {value}
            </div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </Card>
    );
}

export default function DashboardPage() {
    const { workspace, openBusinessSetup } = useOutletContext() || {};
    const [dismissedBanner, setDismissedBanner] = useState(() => {
        try { return localStorage.getItem('billlens.dismissBizBanner') === '1'; }
        catch { return false; }
    });
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [expenseSummary, setExpenseSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useSharedPeriod();

    async function load(p = period) {
        setLoading(true);
        try {
            const params = periodToParams(p);
            // For expenses (which still uses `month` key), fall back to the
            // current calendar month when a preset is picked so the summary
            // remains meaningful. Skip expenses if range is "all time".
            const expenseMonth = (() => {
                if (!p || p.preset === 'all') return null;
                if (p.from) return p.from.slice(0, 7); // first month of range
                return null;
            })();
            const [s, a, exp] = await Promise.all([
                api.stats(params),
                api.listAlerts(),
                expenseMonth
                    ? api.listExpenses({ month: expenseMonth, limit: 1 }).catch(() => null)
                    : api.listExpenses({ limit: 1 }).catch(() => null),
            ]);
            setStats(s);
            setAlerts(a);
            setExpenseSummary(exp);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(period); // eslint-disable-next-line
    }, [period?.from, period?.to, period?.preset]);

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="dashboard-hero rounded-2xl px-4 py-2">
                <PageHeader
                    title="Dashboard"
                    subtitle={
                        period && period.preset !== 'all'
                            ? `Showing bills, spend and alerts for ${period.label}. Change the period from the picker.`
                            : `Showing all-time totals. Change the period from the picker.`
                    }
                    actions={
                        <div className="flex items-center gap-2">
                            <PeriodPicker value={period} onChange={setPeriod} testid="dashboard-period-picker" />
                            <Link to="/upload">
                                <Button className="gap-2" data-testid="dashboard-upload-cta">
                                    <Upload size={16} />
                                    Upload Bill
                                </Button>
                            </Link>
                        </div>
                    }
                />
            </div>

            {/* Sprint 2026-01 Sprint 2: Month-end reconciliation nudge */}
            <MonthEndReconciliationBanner />

            {!workspace && !dismissedBanner && (
                <div
                    className="rounded-xl border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] px-4 py-3 flex items-center gap-3"
                    data-testid="business-profile-banner"
                >
                    <Building2 size={18} className="shrink-0" />
                    <div className="flex-1 text-sm">
                        <b>Set up your business profile</b> — add your PAN (and GSTIN if you have one)
                        so we can flag bills that don't belong to your business.
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBusinessSetup?.()}
                        data-testid="banner-setup-btn"
                    >
                        Set up now
                    </Button>
                    <button
                        type="button"
                        onClick={() => {
                            setDismissedBanner(true);
                            try { localStorage.setItem('billlens.dismissBizBanner', '1'); } catch {}
                        }}
                        className="text-current opacity-70 hover:opacity-100 transition-opacity"
                        aria-label="Dismiss"
                        data-testid="banner-dismiss-btn"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Sprint 2026-07 fix: stale-while-revalidate for KPI cards.
                    Skeletons only appear on the INITIAL page load (before any
                    stats have arrived). On subsequent period changes we KEEP
                    the previous values visible and just soften opacity — so
                    the numbers don't flash to ₹0 and jump around every time
                    you tap the period picker. */}
                {loading && stats === null ? (
                    <>
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28" />
                    </>
                ) : (
                    <div
                        className={`contents transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}
                        aria-busy={loading}
                    >
                        <Kpi icon={Receipt} label="Total bills" value={stats?.total_bills ?? 0} testid="kpi-total-bills" />
                        <Kpi icon={DollarSign} label="Total spend" value={fmtMoney(stats?.total_spend ?? 0)} testid="kpi-total-spend" />
                        <Kpi
                            icon={HandCoins}
                            label="Total payments"
                            value={fmtMoney(stats?.total_payments ?? 0)}
                            hint={
                                stats?.payments_count
                                    ? `${stats.payments_count} payment${stats.payments_count !== 1 ? 's' : ''}`
                                    : 'Paid to vendors'
                            }
                            testid="kpi-total-payments"
                        />
                        <Kpi
                            icon={Bell}
                            label="Active alerts"
                            value={stats?.active_alerts ?? 0}
                            hint={stats?.total_alerts ? `${stats.total_alerts} total` : undefined}
                            testid="kpi-active-alerts"
                        />
                        <Kpi
                            icon={Wallet}
                            label="Expenses"
                            value={fmtMoney(expenseSummary?.total ?? 0)}
                            hint={
                                expenseSummary?.draft_count
                                    ? `${expenseSummary.draft_count} draft${expenseSummary.draft_count !== 1 ? 's' : ''} pending`
                                    : expenseSummary?.count
                                        ? `${expenseSummary.count} logged`
                                        : 'Rent, wifi, chai…'
                            }
                            testid="kpi-expenses"
                        />
                    </div>
                )}
            </div>

            {/* Secondary KPI: Top vendor lives here now (moved down per user request) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <Skeleton className="h-24" />
                ) : (
                    <Kpi
                        icon={Store}
                        label="Top vendor"
                        value={(stats?.top_vendor || '—').toUpperCase()}
                        testid="kpi-top-vendor"
                    />
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2 p-5 border-border/80">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-lg">Recent bills</h2>
                        <Link to="/bills">
                            <Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-view-all-bills">
                                View all <ArrowRight size={14} />
                            </Button>
                        </Link>
                    </div>
                    {loading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-10" />
                            ))}
                        </div>
                    ) : (stats?.recent_bills || []).length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="overflow-hidden rounded-lg border border-border">
                            <table className="w-full text-sm" data-testid="dashboard-recent-bills-table">
                                <thead className="bg-muted/40 text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Vendor</th>
                                        <th className="text-left px-3 py-2 font-medium">Bill Date</th>
                                        <th className="text-right px-3 py-2 font-medium">Items</th>
                                        <th className="text-right px-3 py-2 font-medium">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recent_bills.map((b) => (
                                        <tr key={b.id} className="border-t border-border hover:bg-muted/40">
                                            <td className="px-3 py-2">
                                                <Link
                                                    className="text-foreground hover:underline uppercase tracking-wide"
                                                    to={`/bills/${b.id}`}
                                                    data-testid={`dashboard-recent-bill-link-${b.id}`}
                                                >
                                                    {b.vendor || 'Unknown'}
                                                </Link>
                                                <div className="text-xs text-muted-foreground">
                                                    {b.invoice_number || fmtDateTime(b.uploaded_at)}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">{fmtDate(b.bill_date)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-mono">{(b.items || []).length}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-mono">
                                                {fmtMoney(b.total, b.currency)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="p-5 border-border/80">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-lg">Active alerts</h2>
                        <Link to="/alerts">
                            <Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-view-all-alerts">
                                View all <ArrowRight size={14} />
                            </Button>
                        </Link>
                    </div>
                    {loading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <Skeleton key={i} className="h-12" />
                            ))}
                        </div>
                    ) : alerts.length === 0 ? (
                        <div className="text-sm text-muted-foreground p-3 border border-dashed border-border rounded-md">
                            No price changes yet. Upload another bill to compare against past prices.
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="dashboard-alerts-list">
                            {alerts.slice(0, 6).map((a) => (
                                <li
                                    key={a.id}
                                    className="flex items-start justify-between gap-2 p-2 border border-border/80 rounded-md"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{a.item_name}</div>
                                        <div className="text-xs text-muted-foreground truncate uppercase">{a.vendor}</div>
                                    </div>
                                    <div
                                        className={
                                            'shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-mono tabular-nums ' +
                                            (a.direction === 'increase'
                                                ? 'bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border-[hsl(var(--price-increase-border))]'
                                                : 'bg-[hsl(var(--price-decrease-bg))] text-[hsl(var(--price-decrease-fg))] border-[hsl(var(--price-decrease-border))]')
                                        }
                                    >
                                        {a.direction === 'increase' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {fmtDelta(a.delta)} ({fmtPercent(a.delta_percent)})
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            <Card className="p-5 border-border/80">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading text-lg">Top items you buy</h2>
                    <Link to="/items">
                        <Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-view-items">
                            View items <ArrowRight size={14} />
                        </Button>
                    </Link>
                </div>
                {loading ? (
                    <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-10" />
                        ))}
                    </div>
                ) : (stats?.top_items || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">Items will appear here after your first extraction.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {stats.top_items.map((it, idx) => (
                            <Link
                                key={`${it.vendor_key}-${it.item_key}-${idx}`}
                                to={`/items/detail?vendor_key=${encodeURIComponent(it.vendor_key || '')}&item_key=${encodeURIComponent(it.item_key || '')}`}
                                className="block p-3 rounded-md border border-border hover:bg-muted/40 transition-colors"
                            >
                                <div className="text-sm font-medium truncate">{it.item_name}</div>
                                <div className="text-xs text-muted-foreground truncate uppercase">{it.vendor}</div>
                                <div className="mt-2 flex items-center justify-between">
                                    <Badge variant="secondary" className="font-mono tabular-nums">
                                        Latest {fmtMoney(it.latest_price)}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">{it.count} bills</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center gap-3 p-8 border border-dashed border-border rounded-lg text-center">
            <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center text-accent-foreground">
                <Receipt size={20} />
            </div>
            <div className="font-heading text-base">No bills yet</div>
            <p className="text-sm text-muted-foreground max-w-md">
                Upload your first bill — photo or PDF — and Ease My Bill will extract vendor, date, items, and prices
                automatically.
            </p>
            <Link to="/upload">
                <Button data-testid="empty-state-upload-cta" className="gap-2">
                    <Upload size={16} />
                    Upload your first bill
                </Button>
            </Link>
        </div>
    );
}
